import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  fetchEntitlements,
  fetchFeatureFlags,
  fetchReportConfigs,
  ReportConfig,
  ReportingContext,
} from 'services/reportingContextService'
import { reportException } from 'tools/logs'
import { REPORTING_FLAG_PREFIX } from 'constants/reporting'

const TTL_MS = Number(process.env.REACT_APP_REPORTING_CONTEXT_TTL_MS ?? 60_000)

// Module-level cache — survives provider remounts (e.g. auth logout/login without full page reload)
let cachedContext: ReportingContext = { flags: {}, reportConfigs: {} }
let lastFetchedAt = 0
let pendingFetch: Promise<void> | null = null
let pendingCompanyId: string | null = null
let cachedIsReady = false
// Entitlements belong to the logged-in company, so a login as another company must not reuse them.
let cachedCompanyId: string | null = null

const EMPTY_CONTEXT: ReportingContext = { flags: {}, reportConfigs: {} }
const ENTITLEMENT_PREFIX = 'entitlement.'
const currentCompanyId = (): string | null => localStorage.getItem('companyId')
const cacheIsCurrent = (): boolean => cachedCompanyId === currentCompanyId()
const withoutEntitlements = (flags: Record<string, boolean>): Record<string, boolean> =>
  Object.fromEntries(Object.entries(flags).filter(([key]) => !key.startsWith(ENTITLEMENT_PREFIX)))

interface ReportingContextValue {
  context: ReportingContext
  isReady: boolean
  refreshIfStale: () => void
}

const ReportingCtx = createContext<ReportingContextValue>({
  context: cachedContext,
  isReady: cachedIsReady,
  refreshIfStale: () => {},
})

export const ReportingContextProvider: React.FC<{ children: React.ReactNode }> =
  ({ children }) => {
    const [context, setContext] = useState<ReportingContext>(() => (cacheIsCurrent() ? cachedContext : EMPTY_CONTEXT))
    const [isReady, setIsReady] = useState<boolean>(() => cachedIsReady && cacheIsCurrent())

    const refreshIfStale = useCallback(() => {
      const companyId = currentCompanyId()
      if (companyId !== cachedCompanyId) {
        cachedContext = EMPTY_CONTEXT
        cachedIsReady = false
        setContext(EMPTY_CONTEXT)
        setIsReady(false)
      } else if (cachedIsReady && Date.now() - lastFetchedAt < TTL_MS) {
        return
      }
      if (pendingFetch && pendingCompanyId === companyId) return

      pendingCompanyId = companyId
      const request: Promise<void> = Promise.all([
        fetchFeatureFlags(REPORTING_FLAG_PREFIX),
        fetchReportConfigs(),
        // Fails closed: without an answer every paid feature stays hidden.
        fetchEntitlements().catch((error) => {
          reportException(error)
          return {} as Record<string, boolean>
        }),
      ])
        .then(([flags, reportConfigs, entitlements]) => {
          if (pendingFetch !== request || companyId !== currentCompanyId()) return
          cachedContext = { flags: { ...flags, ...entitlements }, reportConfigs }
          cachedCompanyId = companyId
          cachedIsReady = true
          lastFetchedAt = Date.now()
          setContext(cachedContext)
          setIsReady(true)
        })
        .catch((error) => {
          reportException(error)
          if (pendingFetch !== request || companyId !== currentCompanyId()) return
          cachedContext =
            companyId === cachedCompanyId
              ? { ...cachedContext, flags: withoutEntitlements(cachedContext.flags) }
              : EMPTY_CONTEXT
          cachedCompanyId = companyId
          setContext(cachedContext)
          cachedIsReady = true
          setIsReady(true)
        })
        .finally(() => {
          if (pendingFetch !== request) return
          pendingFetch = null
          pendingCompanyId = null
          if (companyId !== currentCompanyId()) refreshIfStale()
        })
      pendingFetch = request
    }, [])

    useEffect(() => {
      refreshIfStale()
      const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === 'companyId') refreshIfStale()
      }
      window.addEventListener('storage', onStorage)
      return () => {
        window.removeEventListener('storage', onStorage)
        pendingFetch = null
      }
    }, [refreshIfStale])

    return (
      <ReportingCtx.Provider value={{ context, isReady, refreshIfStale }}>
        {children}
      </ReportingCtx.Provider>
    )
  }

export const useFeatureFlags = (): Record<string, boolean> => useContext(ReportingCtx).context.flags

export const useFeatureFlag = (key: string): boolean => {
  const { context } = useContext(ReportingCtx)
  return context.flags[key] ?? false
}

export const useReportConfig = (key: string): ReportConfig | undefined => {
  const { context } = useContext(ReportingCtx)
  return context.reportConfigs[key]
}

export const useIsReportingReady = (): boolean => {
  const { isReady } = useContext(ReportingCtx)
  return isReady
}

export const useRefreshReportingContext = (): (() => void) => {
  const { refreshIfStale } = useContext(ReportingCtx)
  return refreshIfStale
}
